<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1100px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        block
        color="hrissecondary"
        class="white--text"
        v-on="on"
      >
        Create Office Location
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Office Location"
      text="Create Office Location"
      class="mb-0"
    >
      <form v-if="dialog">
        <v-container>
          <v-row no-gutters>
            <v-col
              cols="6"
              class="py-0 mt-n1"
            >
              <v-text-field
                v-model="name"
                v-validate="'required'"
                :error-messages="errors.collect('name')"
                data-vv-name="name"                
                label="Name"
              />
            </v-col>

            <v-col
              cols="3"
              class="pl-3 py-0 mt-n1"
            >
              <v-text-field
                v-model="latitude"
                v-validate="'required'"
                :error-messages="errors.collect('latitude')"
                data-vv-name="latitude"                
                label="Latitude"
                type="number"
                @keydown.native="['e', 'E', '+'].includes($event.key) && $event.preventDefault()"
                @change="latitude && longitude ? locationMarker() : ''"
              />
            </v-col>

            <v-col
              cols="3"
              class="pl-3 py-0 mt-n1"
            >
              <v-text-field
                v-model="longitude"
                v-validate="'required'"
                :error-messages="errors.collect('longitude')"
                data-vv-name="longitude"                
                label="Longitude"
                type="number"
                @keydown.native="['e', 'E', '+'].includes($event.key) && $event.preventDefault()"
                @change="latitude && longitude ? locationMarker() : ''"
              />
            </v-col>

            <v-col
              cols="12"
              class="py-0 my-0"
            >
              <v-row>
                <v-col
                  cols="6"
                  class="py-0 my-0"
                >
                  <span>Acceptable radius follow radius value set in system setting? </span>
                  <v-radio-group
                    v-model="followSetting"
                    class="mt-0"
                    row
                    @change="acceptableRadius = null"
                  >
                    <v-radio
                      :label="'Yes, ' + radiusSettingValue + ' m'"
                      :value="1"
                    />
                    <v-radio
                      label="No, overwrite it"
                      :value="0"
                    /> 
                  </v-radio-group>
                </v-col>
                <v-col
                  v-if="!followSetting"
                  cols="3"
                  class="py-0 my-0"
                >
                  <v-text-field
                    v-model="acceptableRadius"
                    v-validate="'required|integer'"
                    :error-messages="errors.collect('acceptableRadius')"
                    data-vv-name="acceptableRadius"                
                    data-vv-as="acceptable radius"                
                    label="Radius value in metre"
                    type="number"
                    min="0"
                    suffix="m"
                    @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
                  />
                </v-col>
              </v-row>
            </v-col>

            <v-col
              cols="12"
              class="py-0 my-0"
            >
              <v-autocomplete 
                v-model="selectedCompanies"
                label="Company"
                :items="companies"
                item-text="name"  
                item-value="id"
                clearable
                :error-messages="errors.collect('selectedCompanies')"
                data-vv-name="selectedCompanies"  
                data-vv-as="companies"  
                hide-no-data
                hide-selected
                chips
                multiple
                deletable-chips
              />  
            </v-col>

            <v-col
              cols="12"
              class="py-0 my-0"
            >
              <v-row>
                <v-col
                  cols="6"
                  class="py-0 my-0"
                >
                  <v-text-field
                    v-model="addressLine1"
                    v-validate="postcode?'required':''"
                    :error-messages="errors.collect('addressLine1')"
                    data-vv-name="addressLine1"
                    data-vv-as="address"
                    label="Address Line 1"
                    class="py-0 my-0"
                    @change="postcode && city ? getCoordinatesFromAddress() : ''"
                  />
                  <v-text-field
                    v-model="addressLine2"
                    :disabled="!addressLine1"
                    label="Address Line 2"
                    class="py-0 my-0"
                    @change="postcode && city ? getCoordinatesFromAddress() : ''"
                  />
                  <v-text-field
                    v-model="addressLine3"
                    :disabled="!addressLine1"
                    label="Address Line 3"
                    class="py-0 my-0"
                    @change="postcode && city ? getCoordinatesFromAddress() : ''"
                  />
                  <v-autocomplete
                    v-model="postcode"
                    v-validate="addressLine1?'required':''"
                    :items="postcodes"
                    item-text="postcode"
                    item-value="postcode"
                    value="postcode"
                    :error-messages="errors.collect('postcode')"
                    data-vv-name="postcode"
                    data-vv-as="postcode"
                    label="Postcode"
                    class="py-0 my-0"
                    @input="postCodeOnChanged(postcode)"
                    @change="city ? getCoordinatesFromAddress() : ''"
                  />
                  <v-autocomplete
                    v-model="city"
                    v-validate="postcode?'required':''"
                    :disabled="dropdownDisabled"
                    :items="filteredCities"
                    :error-messages="errors.collect('city')"
                    data-vv-name="city"
                    data-vv-as="city"
                    item-text="name"
                    item-value="id"
                    value="id"
                    label="City"
                    class="py-0 my-0"
                    hide-selected
                    @input="cityOnChanged(city)"
                    @change="getCoordinatesFromAddress()"
                  />
                  <v-autocomplete
                    v-model="state"
                    v-validate="city?'required':''"
                    :error-messages="errors.collect('state')"
                    data-vv-name="state"
                    data-vv-as="state"
                    disabled
                    :items="states"
                    item-text="name"
                    item-value="id"
                    value="id"
                    label="State"
                    class="py-0 my-0"
                    hide-selected
                  />
                </v-col>

                <v-col
                  cols="6"
                  class="py-0 my-0"
                >
                  <i style="color:red">*Please mark the location on the map below for better precision.</i>
                  <v-col
                    v-if="gpsEnabled"
                    cols="12"
                    class="py-0 my-0"
                  >
                    <GmapMap
                      :center="location"
                      :zoom="15"
                      map-type-id="roadmap"
                      style="height: 300px"
                      :options="{
                        zoomControl: false,
                        mapTypeControl: false,
                        scaleControl: false,
                        streetViewControl: true,
                        rotateControl: false,
                        fullscreenControl: false,
                        disableDefaultUI: false
                      }"
                    >
                      <GmapMarker
                        :position="location"
                        :clickable="true"
                        :draggable="true"
                        @dragend="getCoordinates($event.latLng)"
                      />
                    </GmapMap> 
                  </v-col>
                  <v-col
                    v-else
                    cols="12"
                  >
                    Please Enable Location Tracking
                  </v-col>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </form>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"
          class="white--text"
          @click="submitForm"
        >
          Create
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import axios from 'axios'
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      radiusSettingValue: {
        type: String,
        default: null,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        followSetting: 1,
        acceptableRadius: 0,
        name: null,
        latitude: null,
        longitude: null,
        addressLine1: null,
        addressLine2: null,
        addressLine3: null,
        postcode: null,
        city: null,
        state: null,

        marker: { lat: 0, lng: 0 },
        defaultLatitude: 0,
        defaultLongitude: 0,
        gpsEnabled: false,
        dropdownDisabled: null,
        cityIds: [],

        selectedCompanies: [],
        companies: [],
        postcodes: [],
        cities: [],
        states: [],
      }
    },
    computed: {
      filteredCities () {
        return this.cities.filter(city => {
          return this.cityIds.includes(city.id)
        })         
      },
      location () {
        if (!this.defaultLatitude || !this.defaultLongitude) return 

        return {
          lat: this.defaultLatitude,
          lng: this.defaultLongitude,
        }
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        this.resetForm()
        this.loadCompanies()
        this.getLocationGPS()

        Promise.all([
          this.$api.states().list(),
          this.$api.cities().list(),
          this.$api.postcodes().list(),
        ])
          .then(([states, cities, postcodes]) => {
            this.states = states.data
            this.cities = cities.data
            this.postcodes = _.orderBy(postcodes.data, ['postcode'], ['asc'])
          })
      },
    },
    methods: { 
      loadCompanies () {
        this.$api.hrisCompanies().list()
          .then(res => {
            this.companies = res.data
          })
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true
            this.$api.officeLocations().create(this.prepareFormData())
              .then(response => {
                if (response.status === 201) {
                  this.$emit('created')
                  this.$snack({ message: 'Office Location created successfully' })

                  this.resetForm()
                  this.dialog = false
                }
              })
              .catch(() => {})
              .then(() => {
                this.progressing = false
              })
          })
      },
      getCoordinates (location) {
        this.latitude = location.lat()
        this.longitude = location.lng()
      },
      getCoordinatesFromAddress () {
        let axios2 = axios.create()

        delete axios2.defaults.headers.common['Authorization']
        axios2.post('https://maps.googleapis.com/maps/api/geocode/json?address=' + this.constructAddress() + '&key=AIzaSyCdnyWg9C4vvfw6YAjyasELkFUqXya8huA')
          .then(res => {
            if (res.data.status === 'OK') {
              res.data.results.map(data => {
                this.latitude = data.geometry.location.lat
                this.longitude = data.geometry.location.lng
                this.marker.lat = this.latitude
                this.marker.lng = this.longitude
                this.defaultLatitude = this.latitude
                this.defaultLongitude = this.longitude
              })
            } else {
              this.$snack({ 
                color: 'error',
                message: 'GPS coordinate not found. Please mark the correct location', 
              })
            }
          })
      },
      locationMarker () {
        this.defaultLatitude = Number(this.latitude)
        this.defaultLongitude = Number(this.longitude)
        this.marker.lat = this.latitude
        this.marker.lng = this.longitude
      },
      constructAddress () {
        return this.checkStringHasComma(this.addressLine1) + ', ' +
          (this.addressLine2 ? this.addressLine2 + ', ' : '') +
          (this.addressLine3 ? this.addressLine3 + ', ' : '') + this.postcode + ' ' + this.getCity(this.city).name + ', ' + this.getState(this.state).name
      },
      checkStringHasComma (string) {
        if (string) {
          if (string.charAt(string.length - 1) === ',') {
            return string.substring(0, string.length - 1)
          }
        }
        return string
      },
      prepareFormData () {
        return {
          name: this.name,
          latitude: this.latitude,
          longitude: this.longitude,
          is_follow_setting_radius: this.followSetting ? 1 : 0,
          acceptable_radius: this.followSetting ? null : this.acceptableRadius,
          company_uids: this.selectedCompanies,
          address_line_one: this.addressLine1,
          address_line_two: this.addressLine2,
          address_line_three: this.addressLine3,
          postcode: this.postcode,
          city_id: this.city,
          state_id: this.state,
        }
      },
      getLocationGPS () {
        if (navigator.geolocation) {
          let _this = this
          navigator.geolocation.getCurrentPosition(function (position) {
            _this.latitude = position.coords.latitude
            _this.longitude = position.coords.longitude

            _this.defaultLatitude = position.coords.latitude
            _this.defaultLongitude = position.coords.longitude

            _this.gpsEnabled = true
          })
        }
      
        this.gpsEnabled = false
      },
      resetForm () {
        this.$validator.reset()
        this.name = null
        this.latitude = null
        this.longitude = null
        this.selectedCompanies = []
        this.addressLine1 = null
        this.addressLine2 = null
        this.addressLine3 = null
        this.postcode = null
        this.city = null
        this.state = null
      },
      postCodeOnChanged (postcodeValue) {
        this.city = null
        this.state = null
        
        var cityID = this.postcodes.filter(p => {
          return p.postcode === postcodeValue
        })  

        this.cityIds = this.cities.filter(city => {
          return cityID.some(city2 => {
            return city2.city_id === city.id
          })
        }).map(city3 => {
          return city3.id
        })

        if (this.cityIds.length > 1) {
          this.dropdownDisabled = false
        } else {
          this.dropdownDisabled = true
          
          let cityId = (this.postcodes.find(postcode => {
            return postcode.postcode === postcodeValue
          }) || {}).city_id

          if (cityId) {
            var cityObject = this.getCity(cityId)
            if (cityObject) {
              this.city = cityObject.id
              var stateObject = this.getState(cityObject.state_id)
              
              if (stateObject) {
                this.state = stateObject.id
              }
            }
          }
        }
      },
      cityOnChanged (cityId) {
        var cityObject = this.getCity(cityId)
        if (cityObject) {
          var stateObject = this.getState(cityObject.state_id)
          
          if (stateObject) {
            this.state = stateObject.id
          }
        }
      },
      getCity (cityId) {
        if (isNaN(parseInt(cityId))) return null
        return this.cities.find(city => {
          return city.id === parseInt(cityId)
        })
      },
      getState (stateId) {
        if (isNaN(parseInt(stateId))) return null
        return this.states.find(state => {
          return state.id === parseInt(stateId)
        })
      },
    },
  }
</script>
